import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ContactPhone as ContactPhoneIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import ContactInformation from "./sections/contactInformation";
import IdentificationDocuments from "./sections/identificationDocuments";
import FinancialInfo from "./sections/financialInfo";
import {
  updateProspectKycStep,
  getProspectKyc,
} from "../../../services/prospectKyc.service";
import { getLoginUserData } from "../../../services/account.service";
import Loader from "../../../common/loader";
import { FinancialInfoStatus } from "../../../constants/general.constant";
import { ProspectKycFormValues } from "./types";
import { uploadDocumentToCloudinary } from "../../../services/cloudinary.service";
import { useSearchParams } from "react-router-dom";
import GuidedTutorialOverlay, {
  GuidedTutorialStep,
} from "../../common/GuidedTutorialOverlay";

// --- Validation --- (Documents, Contact, Financial)
const stepSchemas = [
  // Step 0: Documents (first)
  Yup.object({
    idNumber: Yup.string().required("ID Number is required"),
    expirationDate: Yup.date().required("Expiration Date is required"),
    idType: Yup.string().required("ID Type is required"),
    ssn: Yup.string()
      .required("SSN is required")
      .matches(/^\d{3}-\d{2}-\d{4}$/, "SSN must be in XXX-XX-XXXX format"),
    govFile: Yup.mixed().nullable().required("Government ID file is required"),
    addressFile: Yup.mixed()
      .nullable()
      .required("Proof of Address is required"),
  }),
  // Step 1: Contact Info (second)
  Yup.object({
    firstName: Yup.string().required("First Name is required"),
    middleName: Yup.string(),
    lastName: Yup.string().required("Last Name is required"),
    dob: Yup.date().required("Date of Birth is required"),
    city: Yup.string().required("City is required"),
    street: Yup.string().required("Street is required"),
    state: Yup.string().required("State is required"),
    zipcode: Yup.string().required("Zipcode is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email address is required"),
  }),
  // Step 2: Financial Info (third)
  Yup.object({
    personalIncome: Yup.number()
      .typeError("Must be a number")
      .min(0, "Income cannot be negative")
      .required("Personal income is required"),
    businessRevenue: Yup.number()
      .typeError("Must be a number")
      .min(0, "Revenue cannot be negative")
      .required("Business revenue is required"),
    sourceOfFunds: Yup.string().required("Source of funds is required"),
    financialInfoOption: Yup.string()
      .transform((value) => value === null || value === undefined || value === '' ? FinancialInfoStatus.FILL_FORM : value)
      .oneOf([FinancialInfoStatus.FILL_FORM, FinancialInfoStatus.FILE_UPLOAD])
      .required(),
    financialInfoFile: Yup.mixed().when('financialInfoOption', {
      is: FinancialInfoStatus.FILE_UPLOAD,
      then: (schema) => schema
        .required("Financial statement PDF is required")
        .test('fileSize', 'File size must be less than 10MB', (value) => {
          if (!value || !(value instanceof File)) return true;
          return value.size <= 10 * 1024 * 1024;
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
    digitalSignature: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILE_UPLOAD,
      then: (schema) => schema.required("Digital signature is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    firstName: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("First Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    middleName: Yup.string(),
    lastName: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Last Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    businessPhone: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Business phone is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    residenceAddress: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Residence address is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    residenceCity: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("City is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    residenceState: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("State is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    residenceZipCode: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Zip code is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    residencePhone: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Residence phone is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    businessName: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Business name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    cashOnHandAndBanks: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Cash on hand & in banks is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    savingsAccounts: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Savings accounts is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    iraOrOtherRetirement: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("IRA or other retirement account is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    accountsAndNotesReceivable: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Accounts & notes receivable is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    lifeInsuranceCashSurrender: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Life insurance cash surrender value is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    stocksAndBonds: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Stocks and bonds is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    realEstate: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Real estate is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    automobileTotalPresentValue: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Automobile total present value is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherPersonalProperty: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Other personal property is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherAssets: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Other assets is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    accountsPayable: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Accounts payable is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    notesPayableToBanksAndOthers: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Notes payable to banks and others is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    installmentAccountAuto: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Installment account (auto) is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    moPaymentsAuto: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Mo. payments (auto) is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    moPaymentsOther: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Mo. payments (other) is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    installmentAccountOther: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Installment account (other) is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    loansOnLifeInsurance: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Loans on life insurance is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    mortgagesOnRealEstate: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Mortgages on real estate is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    unpaidTaxes: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Unpaid taxes is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherLiabilities: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Other liabilities is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    salary: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Salary is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    netInvestmentIncome: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Net investment income is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    realEstateIncome: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Real estate income is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherIncome: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Other income is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    asEndorserOrCoMaker: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("As endorser or co-maker is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    legalClaimsAndJudgments: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Legal claims & judgments is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    provisionForFederalIncomeTax: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Provision for federal income tax is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherSpecialDebt: Yup.string().when('financialInfoOption', {
      is: FinancialInfoStatus.FILL_FORM,
      then: (schema) => schema.required("Other special debt is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
];

const initialValues: ProspectKycFormValues = {
  firstName: "",
  middleName: "",
  lastName: "",
  dob: "",
  city: "",
  street: "",
  state: "",
  zipcode: "",
  idNumber: "",
  expirationDate: "",
  phoneNumber: "",
  email: "",
  idType: "",
  govFile: null,
  addressFile: null,
  ssn: "",
  personalIncome: "",
  businessRevenue: "",
  sourceOfFunds: "",
  businessPhone: "",
  residenceAddress: "",
  residenceCity: "",
  residenceState: "",
  residenceZipCode: "",
  residencePhone: "",
  businessName: "",
  cashOnHandAndBanks: "0",
  savingsAccounts: "0",
  iraOrOtherRetirement: "0",
  accountsAndNotesReceivable: "0",
  lifeInsuranceCashSurrender: "0",
  stocksAndBonds: "0",
  realEstate: "0",
  automobileTotalPresentValue: "0",
  otherPersonalProperty: "0",
  otherAssets: "0",
  totalAssets: "0",
  accountsPayable: "0",
  notesPayableToBanksAndOthers: "0",
  installmentAccountAuto: "0",
  moPaymentsAuto: "0",
  installmentAccountOther: "0",
  moPaymentsOther: "0",
  loansOnLifeInsurance: "0",
  mortgagesOnRealEstate: "0",
  unpaidTaxes: "0",
  otherLiabilities: "0",
  totalLiabilities: "0",
  salary: "",
  netInvestmentIncome: "",
  realEstateIncome: "",
  otherIncome: "",
  asEndorserOrCoMaker: "",
  legalClaimsAndJudgments: "",
  provisionForFederalIncomeTax: "",
  otherSpecialDebt: "",
  descriptionOfOtherIncome: "",
  financialInfoFile: undefined,
  digitalSignature: "",
  financialInfoOption: FinancialInfoStatus.FILL_FORM,
  netWorth: "0",
  
  // Joint Account Information - JSON object
  jointAccount: {
    firstName: "",
    middleName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
  },
  
  // Joint Personal Information - JSON object
  jointPersonalInfo: {
    email: "",
    ssn: "",
    dateOfBirth: "",
  },
  
  // Joint Personal Income - JSON object
  jointPersonalIncome: {
    annualIncome: "",
    employmentStatus: "",
    employerFirstName: "",
    employerMiddleName: "",
    employerLastName: "",
    employerPhone: "",
    jobTitle: "",
    yearsEmployed: "",
  },
};

const sections = [
  { label: "Documents", icon: DescriptionIcon, render: IdentificationDocuments },
  { label: "Contact Info", icon: ContactPhoneIcon, render: ContactInformation },
  { label: "Financial Info", icon: AccountBalanceIcon, render: FinancialInfo },
];

const tutorialSteps = [
  {
    stepIndex: 0,
    title: "Documents",
    description:
      "Upload your government ID, proof of address, SSN, and ID details here. These documents help verify your identity before lenders review your request.",
  },
  {
    stepIndex: 1,
    title: "Contact Info",
    description:
      "Confirm your name, date of birth, address, phone number, and email so your KYC profile matches your account details.",
  },
  {
    stepIndex: 2,
    financialInfoOption: FinancialInfoStatus.FILL_FORM,
    title: "Financial Info - Fill Form",
    description:
      "Use Fill Form to enter your income, business revenue, assets, liabilities, source of funds, and signature directly in the KYC form.",
  },
  {
    stepIndex: 2,
    financialInfoOption: FinancialInfoStatus.FILE_UPLOAD,
    title: "Financial Info - Upload PDF",
    description:
      "Use Upload PDF when you already have a financial statement. Upload the PDF and add your digital signature before saving this section.",
  },
];

// Rest of component unchanged...
interface Props {
  customer_id?: string;
}

const ProspectKYCForm: React.FC<Props> = ({ customer_id = null }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionContentRef = useRef<HTMLDivElement | null>(null);
  const [customerId, setCustomerId] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [completion, setCompletion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialFormValues, setInitialFormValues] = useState<ProspectKycFormValues>(initialValues);
  const [successMessage, setSuccessMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("success");
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(
    Array(sections.length).fill(false)
  );
  const formikRef = useRef<any>(null);
  const guidedTutorialSteps: GuidedTutorialStep[] = tutorialSteps.map(
    (step) => ({
      ...step,
      targetRef: sectionContentRef,
    }),
  );

  useEffect(() => {
    if (tutorialOpen) {
      const activeTutorialStep = tutorialSteps[tutorialStep];
      setActiveTab(activeTutorialStep.stepIndex);
      if (activeTutorialStep.financialInfoOption) {
        formikRef.current?.setFieldValue(
          "financialInfoOption",
          activeTutorialStep.financialInfoOption,
          false,
        );
      }
    }
  }, [tutorialOpen, tutorialStep]);

  useEffect(() => {
    if (customer_id || searchParams.get("showTutorial") !== "kyc") return;

    setTutorialStep(0);
    setTutorialOpen(true);
    setSearchParams({}, { replace: true });
  }, [customer_id, searchParams, setSearchParams]);

  useEffect(() => {
    const calculateCompletion = async () => {
      let validSteps = 0;

      for (let i = 0; i < stepSchemas.length; i++) {
        try {
          const stepFields = Object.keys(
            stepSchemas[i].fields
          ) as (keyof ProspectKycFormValues)[];
          const stepValues = stepFields.reduce(
            (acc, key) => ({ ...acc, [key]: initialFormValues[key] }),
            {} as Partial<ProspectKycFormValues>
          );

          await stepSchemas[i].validate(stepValues, { abortEarly: false });
          validSteps++;
        } catch {}
      }
      setCompletion(Math.round((validSteps / stepSchemas.length) * 100));
    };

    calculateCompletion();
  }, [initialFormValues, activeTab]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        if (customer_id) {
          setCustomerId(customer_id);
        } else {
          const user = await getLoginUserData();
          if (user?.id) setCustomerId(user.id);
        }
      } catch {
        console.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [customer_id]);

  useEffect(() => {
    const loadKyc = async () => {
      if (!customerId) return;
      setLoading(true);
      try {
        const data = await getProspectKyc(customerId);
        if (data) {
          const mergedValues = {
            ...initialValues,
            ...data,
            financialInfoOption:
              data.financialInfoOption === FinancialInfoStatus.FILL_FORM ||
              data.financialInfoOption === FinancialInfoStatus.FILE_UPLOAD
                ? data.financialInfoOption
                : FinancialInfoStatus.FILL_FORM,
          };

          setInitialFormValues(mergedValues);
          const newCompleted = await Promise.all(
            stepSchemas.map(async (schema) => {
              try {
                await schema.validate(mergedValues, { abortEarly: false });
                return true;
              } catch {
                return false;
              }
            })
          );
          setCompletedSteps(newCompleted);
        }
      } catch (err) {
        console.error("Failed to fetch KYC", err);
      } finally {
        setLoading(false);
      }
    };

    loadKyc();
  }, [customerId]);

  if (loading) return <Loader />;

  const sanitizeFilenamePart = (value: string) =>
    value
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const getFileExtension = (fileName: string) => {
    const match = fileName.match(/\.[^/.]+$/);
    return match ? match[0].toLowerCase() : "";
  };

  const createUploadFileName = (docName: string, file: File) => {
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}Z$/, "");
    const documentName = sanitizeFilenamePart(docName) || "document";
    const originalName = sanitizeFilenamePart(
      file.name.replace(/\.[^/.]+$/, ""),
    );
    const extension = getFileExtension(file.name);

    return `${documentName}_${timestamp}_${originalName}${extension}`;
  };

  const showTutorialAgain = () => {
    setTutorialStep(0);
    setTutorialOpen(true);
  };

  const closeTutorial = () => {
    setTutorialOpen(false);
  };

  const finishTutorial = async () => {
    setTutorialOpen(false);
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep === tutorialSteps.length - 1) {
      finishTutorial();
      return;
    }
    setTutorialStep((step) => step + 1);
  };

  const handleBackTutorialStep = () => {
    setTutorialStep((step) => Math.max(step - 1, 0));
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialFormValues}
        validationSchema={stepSchemas[activeTab]}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={async (values) => {
          if (!customerId) return;
          setLoading(true);
          try {
            const modifiedValues = { ...values };

            // Frontend -> Cloudinary upload for Financial Info PDF
            if (
              modifiedValues.financialInfoOption === FinancialInfoStatus.FILE_UPLOAD &&
              modifiedValues.financialInfoFile instanceof File
            ) {
              const uploadName = createUploadFileName("Financial", modifiedValues.financialInfoFile);
              const uploaded = await uploadDocumentToCloudinary(
                modifiedValues.financialInfoFile,
                'kyc/financial',
                uploadName
              );
              modifiedValues.financialInfoFile = uploaded.secureUrl;
            }

            if (values.financialInfoOption === FinancialInfoStatus.FILL_FORM) {
              modifiedValues.financialInfoFile = undefined;
            } else if (values.financialInfoOption === FinancialInfoStatus.FILE_UPLOAD) {
              const formFields: (keyof ProspectKycFormValues)[] = [
                'firstName', 'middleName', 'lastName', 'businessPhone', 'residenceAddress', 'residenceCity', 'residenceState',
                'residenceZipCode', 'residencePhone', 'businessName', 'cashOnHandAndBanks',
                'savingsAccounts', 'iraOrOtherRetirement', 'accountsAndNotesReceivable',
                'lifeInsuranceCashSurrender', 'stocksAndBonds', 'realEstate',
                'automobileTotalPresentValue', 'otherPersonalProperty', 'otherAssets',
                'totalAssets', 'accountsPayable', 'notesPayableToBanksAndOthers', 'installmentAccountAuto',
                'moPaymentsAuto', 'installmentAccountOther', 'moPaymentsOther',
                'loansOnLifeInsurance', 'mortgagesOnRealEstate', 'unpaidTaxes',
                'otherLiabilities', 'totalLiabilities', 'salary', 'netInvestmentIncome', 'realEstateIncome',
                'otherIncome', 'asEndorserOrCoMaker', 'legalClaimsAndJudgments',
                'provisionForFederalIncomeTax', 'otherSpecialDebt', 'descriptionOfOtherIncome',
                'netWorth'
              ];
              const numericFields = [
                'cashOnHandAndBanks', 'savingsAccounts', 'iraOrOtherRetirement',
                'accountsAndNotesReceivable', 'lifeInsuranceCashSurrender', 'stocksAndBonds',
                'realEstate', 'automobileTotalPresentValue', 'otherPersonalProperty', 'otherAssets',
                'totalAssets', 'accountsPayable', 'notesPayableToBanksAndOthers', 'installmentAccountAuto',
                'moPaymentsAuto', 'installmentAccountOther', 'moPaymentsOther',
                'loansOnLifeInsurance', 'mortgagesOnRealEstate', 'unpaidTaxes',
                'otherLiabilities', 'totalLiabilities', 'netWorth'
              ];
              formFields.forEach(field => {
                if (numericFields.includes(field)) {
                  (modifiedValues as any)[field] = '0';
                } else {
                  (modifiedValues as any)[field] = '';
                }
              });
            }

            await updateProspectKycStep(customerId, activeTab, modifiedValues);
            setInitialFormValues((prev) => ({ ...prev, ...modifiedValues }));
            setCompletedSteps((prev) => {
              const newCompleted = [...prev];
              newCompleted[activeTab] = true;
              return newCompleted;
            });
            setMessageType("success");
            setSuccessMessage("Your KYC documents have been uploaded successfully");
          } catch (error: any) {
            setMessageType("error");
            setSuccessMessage(error?.message || error?.response?.data?.message);
          } finally {
            setLoading(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          validateForm,
          handleSubmit,
          setTouched,
        }) => {
          const StepComponent = sections[activeTab].render;
          
          const handleNext = async () => {
            const stepFields = Object.keys(stepSchemas[activeTab].fields);
            setTouched(
              stepFields.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
              true
            );
            const formErrors = await validateForm();
            const hasStepErrors = stepFields.some(
              (key) => (formErrors as Record<string, any>)[key]
            );

            if (hasStepErrors) {
              const errorFields = stepFields.filter(
                (key) => (formErrors as Record<string, any>)[key]
              );
              setMessageType("error");
              setSuccessMessage(`Please fill all required fields: ${errorFields.join(', ')}`);
              return;
            }

            if (!hasStepErrors && customerId) {
              setLoading(true);
              try {
                const numericFields = [
                  'cashOnHandAndBanks', 'savingsAccounts', 'iraOrOtherRetirement',
                  'accountsAndNotesReceivable', 'lifeInsuranceCashSurrender', 'stocksAndBonds',
                  'realEstate', 'automobileTotalPresentValue', 'otherPersonalProperty', 'otherAssets',
                  'totalAssets', 'accountsPayable', 'notesPayableToBanksAndOthers', 'installmentAccountAuto',
                  'moPaymentsAuto', 'installmentAccountOther', 'moPaymentsOther',
                  'loansOnLifeInsurance', 'mortgagesOnRealEstate', 'unpaidTaxes',
                  'otherLiabilities', 'totalLiabilities', 'netWorth'
                ];
                const processedValues:any = { ...values };

                // Frontend -> Cloudinary upload for Financial Info PDF
                if (
                  processedValues.financialInfoOption === FinancialInfoStatus.FILE_UPLOAD &&
                  processedValues.financialInfoFile instanceof File
                ) {
                  const uploadName = createUploadFileName("Financial", processedValues.financialInfoFile);
                  const uploaded = await uploadDocumentToCloudinary(
                    processedValues.financialInfoFile,
                    'kyc/financial',
                    uploadName
                  );
                  processedValues.financialInfoFile = uploaded.secureUrl;
                }

                numericFields.forEach(field => {
                  if (processedValues[field] === '' || processedValues[field] === undefined) {
                    processedValues[field] = '0';
                  }
                });
                await updateProspectKycStep(customerId, activeTab, processedValues);


                setInitialFormValues((prev) => ({ ...prev, ...processedValues }));
                setCompletedSteps((prev) => {
                  const newCompleted = [...prev];
                  newCompleted[activeTab] = true;
                  return newCompleted;
                });
                if (activeTab < sections.length - 1) {
                  setActiveTab((prev) => prev + 1);
                }
              } catch (error: any) {
                setMessageType("error");
                setSuccessMessage(error?.message || error?.response?.data?.message);
              } finally {
                setLoading(false);
              }
            }
          };

          return (
            <Paper className="p-4 sm:p-6 rounded-2xl shadow-lg bg-white w-full mx-auto">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 sm:mb-6">
                <Typography
                  variant="h6"
                  className="font-bold text-gray-800 bg-blue-50 px-3 sm:px-4 py-2 rounded-lg inline-block text-center sm:text-left"
                >
                  KYC
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<HelpOutlineIcon />}
                  onClick={showTutorialAgain}
                  disabled={tutorialOpen}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Show Tutorial
                </Button>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-600 tracking-wide">
                    Profile Completion
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      completion === 100 ? "text-emerald-600" : "text-blue-600"
                    }`}
                  >
                    {completion}%
                  </span>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={completion}
                  className="rounded-full h-3 bg-gray-200"
                />
              </div>

              <Stepper
                activeStep={activeTab}
                orientation={isDesktop ? "horizontal" : "vertical"}
                alternativeLabel={isDesktop}
                className="mb-4 sm:mb-6"
              >
                {sections.map((s, i) => {
                  const IconComp = s.icon;
                  const isStepValid = completedSteps[i];

                  return (
                    <Step key={s.label} completed={isStepValid}>
                      <StepLabel
                        StepIconComponent={() => (
                          <IconComp
                            fontSize="small"
                            color={
                              i < activeTab
                                ? "primary"
                                : i === activeTab
                                ? "success"
                                : "disabled"
                            }
                          />
                        )}
                        onClick={() => {
                          if (isStepValid || i < activeTab) {
                            setActiveTab(i);
                          }
                        }}
                        style={{
                          cursor:
                            isStepValid || i < activeTab
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        <Typography variant="body2" className="font-medium">
                          {s.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              <Box className="mt-4" ref={sectionContentRef}>
                <StepComponent
                  customerId={customerId}
                  values={values}
                  errors={errors as any}
                  touched={touched as any}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                />
              </Box>

              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0 mt-6 sm:mt-8">
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setActiveTab((p) => Math.max(0, p - 1))}
                  disabled={activeTab === 0}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    order: { xs: 2, sm: 1 }
                  }}
                >
                  Back
                </Button>
                {activeTab < sections.length - 1 ? (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleNext}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      width: { xs: '100%', sm: 'auto' },
                      order: { xs: 1, sm: 2 }
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      width: { xs: '100%', sm: 'auto' },
                      order: { xs: 1, sm: 2 }
                    }}
                    onClick={async () => {
                      const errors = await validateForm();
                      if (Object.keys(errors).length > 0) {
                        formikRef.current.setErrors(errors);
                        setTouched(Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
                        setMessageType("error");
                        setSuccessMessage(`Please fill all required fields: ${Object.keys(errors).join(', ')}`);
                        return;
                      }
                      handleSubmit();
                    }}
                  >
                    Finish
                  </Button>
                )}
              </div>
            </Paper>
          );
        }}
      </Formik>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => {
          setSuccessMessage("");
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity={messageType}
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <GuidedTutorialOverlay
        open={tutorialOpen}
        steps={guidedTutorialSteps}
        activeStep={tutorialStep}
        onBack={handleBackTutorialStep}
        onNext={handleNextTutorialStep}
        onClose={closeTutorial}
      />
    </>
  );
};

export default ProspectKYCForm;
